import type { LicenseDto } from '@order-pilot/shared';
import { formatDateTime, formatRelative } from '@/lib/format';

/**
 * Which till actually consumed this licence.
 *
 * The row's heading is `label` — what the SA TYPED when issuing. This is
 * `device.label`, what really activated. Without it, revoke / extend are
 * decided from an intention rather than a fact.
 *
 * The two disagreeing is the case worth catching: a licence issued as
 * "Counter 1" and activated on the back-office machine still reads
 * "Counter 1" everywhere else. That is called out rather than shown as an
 * equal alternative, because it usually means the wrong code went to the wrong
 * till and the mirror tables are now keyed to a machine nobody expects.
 */
export function ConsumedByDevice({ license }: { license: LicenseDto }) {
  const device = license.device;

  // The status badge already says "Awaiting activation"; repeating it here
  // would be noise.
  if (!device) return null;

  // Matching names mean the heading ALREADY names the device — repeating it
  // would put the same string on screen twice and train the eye to skip a chip
  // that only matters when it disagrees. Silence here is what makes the amber
  // case read as a signal rather than decoration.
  if (device.label === license.label) return null;

  const seen = device.lastSeenAt
    ? `Last synced ${formatRelative(device.lastSeenAt)} (${formatDateTime(device.lastSeenAt)}).`
    : 'This device has never synced.';

  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-900"
      title={
        `Issued as "${license.label}" but activated on "${device.label}". ` +
        'Usually means the code went to a different till than intended. The ' +
        `reporting history is keyed to this device, so moving it later splits ` +
        `the till's history in two. ${seen}`
      }
    >
      Activated on {device.label}
    </span>
  );
}
